import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Card, CardContent, CircularProgress, IconButton, Button, AppBar, Toolbar, useTheme } from '@mui/material';
import {
  People as PeopleIcon,
  LocationOn as LocationIcon,
  CheckCircle as CheckIcon,
  LocalGasStation as FuelIcon,
  Upload as UploadIcon,
  Analytics as AnalyticsIcon,
  TrendingUp as MetricsIcon,
  Description as DocumentIcon,
  ChevronRight as ArrowIcon,
  LocalShipping as EquipmentIcon,
  Assessment as ReportsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { userApi, postApi, dashboardApi } from '../services/api';
import { unwrapSingleResponse } from '../apiUnwrapper/apiUnwrapper';
import { DashboardMetrics } from '../types';
import { json } from 'stream/consumers';

interface DashboardCard {
  title: string;
  subtitle: string;
  icon: React.ReactElement;
  iconBg: string;
  stats?: { label: string; value: number }[];
  action?: { label: string; onClick: () => void };
  path: string;
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    operators: 0,
    equipment: 0,
    loadSites: 3,
    dumpSites: 2,
    materials: 3,
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const metrics = unwrapSingleResponse<DashboardMetrics>(await dashboardApi.getDashboardMetrics());
        setStats({
          operators: metrics?.Operators || 0,
          equipment: metrics?.Equipments || 0,
          loadSites: metrics?.LoadLocations || 0,
          dumpSites: metrics?.DumpLocations || 0,
          materials: metrics?.Materials || 0,
        });
      } catch (error) {
        console.error('Failed to load application statistics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const dashboardCards: DashboardCard[] = [
    {
      title: 'Common Data',
      subtitle: 'Operators, Equipment, Shifts & Crews',
      icon: <PeopleIcon sx={{ fontSize: 32 }} />,
      iconBg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      stats: [
        { label: 'Operators', value: stats.operators },
        { label: 'Equipment', value: stats.equipment },
      ],
      path: '/commondata',
    },
    {
      title: 'Haul Master Live',
      subtitle: 'Load/Dump Locations & Material Catalog',
      icon: <LocationIcon sx={{ fontSize: 32 }} />,
      iconBg: 'linear-gradient(135deg, #06beb6 0%, #48b1bf 100%)',
      stats: [
        { label: 'Load Sites', value: stats.loadSites },
        { label: 'Dump Sites', value: stats.dumpSites },
        { label: 'Materials', value: stats.materials },
      ],
      path: '/haulmasterlive',
    },
    {
      title: 'Pre Checks and Forms',
      subtitle: 'FLHA, Equipment Checklists & NPT Tracking',
      icon: <CheckIcon sx={{ fontSize: 32 }} />,
      iconBg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      action: { label: 'Configure Checklists', onClick: () => navigate('/external') },
      path: '/prechecksandforms',
    },
    {
      title: 'Fuel Management',
      subtitle: 'Fuel Types & Sources Configuration',
      icon: <FuelIcon sx={{ fontSize: 32 }} />,
      iconBg: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      action: { label: 'Manage', onClick: () => navigate('/users') },
      path: '/fueltypesandsources',
    },
    {
      title: 'Plan Uploads',
      subtitle: 'Mine Plans & Documentation',
      icon: <UploadIcon sx={{ fontSize: 32 }} />,
      iconBg: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
      path: '/planupload',
    },
    {
      title: 'Reports & Analytics',
      subtitle: 'Operational, Safety & Performance Reports',
      icon: <AnalyticsIcon sx={{ fontSize: 32 }} />,
      iconBg: 'linear-gradient(135deg, #fad0c4 0%, #ffd1ff 100%)',
      path: '/reports',
    },
    {
      title: 'Dispatch Data Uploads',
      subtitle: 'Existing Dispatch Data & Integration',
      icon: <UploadIcon sx={{ fontSize: 32 }} />,
      iconBg: 'linear-gradient(135deg, #1def43 0%, #fed6e3 100%)',
      path: '/dispatchupload',
    },
    {
      title: 'Documentation Portal',
      subtitle: 'Technical Reports, SOPs & Drawings',
      icon: <DocumentIcon sx={{ fontSize: 32 }} />,
      iconBg: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
      path: '/documents',
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh" sx={{ backgroundColor: theme.palette.background.default }}>
        <CircularProgress sx={{ color: '#667eea' }} />
      </Box>
    );
  }

  const navItems = [
    { label: 'Dashboard', icon: <AnalyticsIcon sx={{ fontSize: 18, mr: 0.5 }} />, path: '/', active: true },
    { label: 'Load Locations', icon: <LocationIcon sx={{ fontSize: 18, mr: 0.5 }} />, path: '/posts' },
    { label: 'Dump Locations', icon: <LocationIcon sx={{ fontSize: 18, mr: 0.5 }} />, path: '/posts' },
    { label: 'Materials', icon: <CheckIcon sx={{ fontSize: 18, mr: 0.5 }} />, path: '/external' },
    { label: 'Operators', icon: <PeopleIcon sx={{ fontSize: 18, mr: 0.5 }} />, path: '/users' },
    { label: 'Equipment', icon: <EquipmentIcon sx={{ fontSize: 18, mr: 0.5 }} />, path: '/users' },
    { label: 'Plan Uploads', icon: <UploadIcon sx={{ fontSize: 18, mr: 0.5 }} />, path: '/posts' },
    { label: 'Reports', icon: <ReportsIcon sx={{ fontSize: 18, mr: 0.5 }} />, path: '/reports' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default }}>

      {/* Main Content */}
      <Box sx={{ pb: 4, pt: 4 }}>

      <Box sx={{ px: 3, maxWidth: 1400, mx: 'auto' }}>
        <Grid container spacing={3}>
          {dashboardCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Card
                sx={{
                  height: '100%',
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: `1px solid ${theme.palette.divider}`,
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: theme.palette.mode === 'light' ? '0 12px 24px rgba(0,0,0,0.1)' : '0 12px 24px rgba(0,0,0,0.3)' },
                }}
                onClick={() => navigate(card.path)}
              >
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: 2, background: card.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                      {card.icon}
                    </Box>
                    <IconButton size="small" sx={{ color: theme.palette.text.secondary }}>
                      <ArrowIcon />
                    </IconButton>
                  </Box>

                  <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 1 }}>
                    {card.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2, flexGrow: 1 }}>
                    {card.subtitle}
                  </Typography>

                  {card.stats && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {card.stats.map((stat, i) => (
                        <Box key={i} sx={{ backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.background.default, px: 2, py: 1, borderRadius: 1, flex: '1 1 auto', minWidth: 60 }}>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                            {stat.label}
                          </Typography>
                          <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 700 }}>
                            {stat.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {card.action && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #475569' }}>
                      <Typography
                        variant="body2"
                        sx={{ color: '#667eea', fontWeight: 600, cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); card.action?.onClick(); }}
                      >
                        {card.action.label}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ mt: 4, px: 3, maxWidth: 1400, mx: 'auto' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2, p: 2, textAlign: 'center', border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h4" sx={{ color: '#06beb6', fontWeight: 700 }}>{stats.loadSites}</Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Load Sites</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2, p: 2, textAlign: 'center', border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h4" sx={{ color: '#06beb6', fontWeight: 700 }}>{stats.dumpSites}</Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Dump Sites</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2, p: 2, textAlign: 'center', border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h4" sx={{ color: '#a855f7', fontWeight: 700 }}>{stats.materials}</Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Materials</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2, p: 2, textAlign: 'center', border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h4" sx={{ color: '#667eea', fontWeight: 700 }}>{stats.equipment}</Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Equipment</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2, p: 2, textAlign: 'center', border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h4" sx={{ color: '#ef4444', fontWeight: 700 }}>{stats.operators}</Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Operators</Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
      </Box>
    </Box>
  );
};

export default HomePage;
